import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Zap,
  Plus,
  Trash2,
  TrendingUp,
  Info,
  AlertTriangle,
  PieChart,
  DollarSign,
  Percent,
} from 'lucide-react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { calcSAFEConversion } from '@/utils/equityMath';

interface SAFE {
  id: string;
  name: string;
  investmentAmount: number;
  valuationCap: number | null;
  discountRate: number;
  safeType: 'post-money' | 'pre-money';
}

interface PricedRound {
  preMoney: number;
  newMoney: number;
  pricePerShare: number;
  optionPoolPct: number; // % of post-money for option pool
}

interface ConversionResult {
  safeName: string;
  investmentAmount: number;
  conversionPrice: number;
  sharesReceived: number;
  ownershipPct: number;
  effectiveValuation: number;
  discount: number;
}

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

export function SafeConversionSimulator() {
  const [safes, setSafes] = useState<SAFE[]>([
    {
      id: '1',
      name: 'SAFE #1',
      investmentAmount: 500000,
      valuationCap: 5000000,
      discountRate: 20,
      safeType: 'post-money',
    },
  ]);

  const [pricedRound, setPricedRound] = useState<PricedRound>({
    preMoney: 10000000,
    newMoney: 3000000,
    pricePerShare: 1.0,
    optionPoolPct: 10,
  });

  // Everything on the register before the round: founders, other holders and the
  // existing option pool. The valuation cap is measured against this, not against
  // the founders' shares alone.
  const [existingShares, setExistingShares] = useState(8000000);

  const addSafe = () => {
    const newSafe: SAFE = {
      id: Date.now().toString(),
      name: `SAFE #${safes.length + 1}`,
      investmentAmount: 0,
      valuationCap: null,
      discountRate: 20,
      safeType: 'post-money',
    };
    setSafes([...safes, newSafe]);
  };

  const removeSafe = (id: string) => {
    setSafes(safes.filter((s) => s.id !== id));
  };

  const updateSafe = (id: string, field: keyof SAFE, value: any) => {
    setSafes(safes.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const calculateConversion = (): {
    results: ConversionResult[];
    founders: { shares: number; pct: number };
    newInvestors: { shares: number; pct: number };
    optionPool: { shares: number; pct: number };
    totalShares: number;
  } => {
    // The maths lives in calcSAFEConversion so it can be tested. This component
    // used to carry its own copy, which divided the valuation cap by the founders'
    // shares alone and read safeType from the first SAFE for the whole stack.
    const converted = calcSAFEConversion(safes, pricedRound, existingShares);
    const newInvestorShares =
      pricedRound.pricePerShare > 0 ? pricedRound.newMoney / pricedRound.pricePerShare : 0;
    const optionPoolShares = (converted.optionPoolPct / 100) * converted.totalShares;

    const results: ConversionResult[] = converted.results.map((r, i) => ({
      safeName: safes[i].name,
      investmentAmount: safes[i].investmentAmount,
      conversionPrice: r.conversionPrice,
      sharesReceived: r.sharesReceived,
      ownershipPct: r.ownershipPct,
      effectiveValuation: r.effectiveValuation,
      discount: r.discount,
    }));

    return {
      results,
      founders: {
        shares: existingShares,
        pct: converted.founderPct,
      },
      newInvestors: {
        shares: newInvestorShares,
        pct: converted.newInvestorPct,
      },
      optionPool: {
        shares: optionPoolShares,
        pct: converted.optionPoolPct,
      },
      totalShares: converted.totalShares,
    };
  };

  const conversion = calculateConversion();
  const totalSafeInvestment = safes.reduce((sum, s) => sum + s.investmentAmount, 0);

  // Prepare data for ownership pie chart
  const ownershipData = [
    { name: 'Founders', value: conversion.founders.pct, shares: conversion.founders.shares },
    ...conversion.results.map((r) => ({
      name: r.safeName,
      value: r.ownershipPct,
      shares: r.sharesReceived,
    })),
    {
      name: 'New Investors',
      value: conversion.newInvestors.pct,
      shares: conversion.newInvestors.shares,
    },
    { name: 'Option Pool', value: conversion.optionPool.pct, shares: conversion.optionPool.shares },
  ];

  // Prepare data for comparison chart
  const comparisonData = conversion.results.map((r) => ({
    name: r.safeName,
    Investment: r.investmentAmount,
    'Effective Valuation': r.effectiveValuation,
    'Round Price': pricedRound.preMoney + pricedRound.newMoney,
  }));

  const postMoney = pricedRound.preMoney + pricedRound.newMoney;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            SAFE Conversion Simulator
          </CardTitle>
          <CardDescription>
            Model how SAFEs convert to equity in priced rounds with valuation caps and discount
            rates
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="safes" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="safes">SAFE Agreements</TabsTrigger>
              <TabsTrigger value="round">Priced Round</TabsTrigger>
              <TabsTrigger value="results">Conversion Results</TabsTrigger>
            </TabsList>

            <TabsContent value="safes" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Outstanding SAFEs</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Add all SAFE agreements that will convert in the next equity round
                    </p>
                  </div>
                  <Button onClick={addSafe} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add SAFE
                  </Button>
                </div>

                {safes.map((safe, idx) => (
                  <Card key={safe.id} className="border-muted">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{safe.name}</CardTitle>
                        <Button
                          onClick={() => removeSafe(safe.id)}
                          size="sm"
                          variant="ghost"
                          disabled={safes.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">SAFE Name</Label>
                          <Input
                            value={safe.name}
                            onChange={(e) => updateSafe(safe.id, 'name', e.target.value)}
                            placeholder="SAFE #1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Investment Amount</Label>
                          <Input
                            type="number"
                            value={safe.investmentAmount}
                            onChange={(e) =>
                              updateSafe(safe.id, 'investmentAmount', Number(e.target.value))
                            }
                            placeholder="500000"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Valuation Cap</Label>
                          <Input
                            type="number"
                            value={safe.valuationCap || ''}
                            onChange={(e) =>
                              updateSafe(
                                safe.id,
                                'valuationCap',
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                            placeholder="5000000 (optional)"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Discount Rate (%)</Label>
                          <Input
                            type="number"
                            value={safe.discountRate}
                            onChange={(e) =>
                              updateSafe(safe.id, 'discountRate', Number(e.target.value))
                            }
                            placeholder="20"
                            min="0"
                            max="100"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs">SAFE Type</Label>
                          <Select
                            value={safe.safeType}
                            onValueChange={(v) => updateSafe(safe.id, 'safeType', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="post-money">Post-Money SAFE</SelectItem>
                              <SelectItem value="pre-money">Pre-Money SAFE (Legacy)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {safe.valuationCap && (
                          <Badge variant="default" className="text-xs">
                            ${(safe.valuationCap / 1000000).toFixed(1)}M Cap
                          </Badge>
                        )}
                        {safe.discountRate > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {safe.discountRate}% Discount
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          {safe.safeType === 'post-money' ? 'Post-Money' : 'Pre-Money'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>Valuation Cap:</strong> Maximum valuation at which SAFE converts. Lower
                    cap = better for investor.
                    <br />
                    <strong>Discount:</strong> Discount off the round price. If both exist, investor
                    gets the better of the two.
                    <br />
                    <strong>Post-Money SAFE (recommended):</strong> Guarantees ownership percentage,
                    simpler math.
                  </AlertDescription>
                </Alert>
              </div>
            </TabsContent>

            <TabsContent value="round" className="space-y-4 mt-4">
              <Card className="border-muted">
                <CardHeader>
                  <CardTitle className="text-sm">Equity Financing Round Details</CardTitle>
                  <CardDescription className="text-xs">
                    Enter the terms of the priced round that will trigger SAFE conversion
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Pre-Money Valuation</Label>
                      <Input
                        type="number"
                        value={pricedRound.preMoney}
                        onChange={(e) =>
                          setPricedRound({ ...pricedRound, preMoney: Number(e.target.value) })
                        }
                        placeholder="10000000"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        ${(pricedRound.preMoney / 1000000).toFixed(1)}M pre-money
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs">New Money Raised</Label>
                      <Input
                        type="number"
                        value={pricedRound.newMoney}
                        onChange={(e) =>
                          setPricedRound({ ...pricedRound, newMoney: Number(e.target.value) })
                        }
                        placeholder="3000000"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        ${(pricedRound.newMoney / 1000000).toFixed(1)}M new investment
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs">Price Per Share</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={pricedRound.pricePerShare}
                        onChange={(e) =>
                          setPricedRound({ ...pricedRound, pricePerShare: Number(e.target.value) })
                        }
                        placeholder="1.00"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        ${pricedRound.pricePerShare.toFixed(4)} per share
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs">Option Pool (%)</Label>
                      <Input
                        type="number"
                        value={pricedRound.optionPoolPct}
                        onChange={(e) =>
                          setPricedRound({ ...pricedRound, optionPoolPct: Number(e.target.value) })
                        }
                        placeholder="10"
                        min="0"
                        max="100"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {pricedRound.optionPoolPct}% of post-money
                      </p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Existing Shares (Fully Diluted, Pre-Round)</Label>
                      <Input
                        type="number"
                        value={existingShares}
                        onChange={(e) => setExistingShares(Number(e.target.value))}
                        placeholder="8000000"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {existingShares.toLocaleString()} shares on the register before conversion —
                        founders, other holders and the existing option pool
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-muted rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Pre-Money Valuation:</span>
                      <span className="font-medium">
                        ${(pricedRound.preMoney / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">New Money:</span>
                      <span className="font-medium">
                        ${(pricedRound.newMoney / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                      <span>Post-Money Valuation:</span>
                      <span className="text-primary">${(postMoney / 1000000).toFixed(2)}M</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Alert>
                <TrendingUp className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  SAFEs convert automatically when the company raises a qualified equity financing
                  round (typically $1M+ threshold). The conversion price is determined by applying
                  the better of the valuation cap or discount rate.
                </AlertDescription>
              </Alert>
            </TabsContent>

            <TabsContent value="results" className="space-y-4 mt-4">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      Total SAFE Capital
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      ${(totalSafeInvestment / 1000000).toFixed(2)}M
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <Percent className="h-3 w-3" />
                      SAFE Ownership
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {conversion.results.reduce((sum, r) => sum + r.ownershipPct, 0).toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      Post-Money
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">${(postMoney / 1000000).toFixed(1)}M</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground flex items-center gap-1">
                      <PieChart className="h-3 w-3" />
                      Total Shares
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {(conversion.totalShares / 1000000).toFixed(2)}M
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Conversion Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">SAFE Conversion Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-semibold">SAFE</th>
                          <th className="text-right p-2 text-sm font-semibold">Investment</th>
                          <th className="text-right p-2 text-sm font-semibold">Conversion Price</th>
                          <th className="text-right p-2 text-sm font-semibold">Discount</th>
                          <th className="text-right p-2 text-sm font-semibold">Shares</th>
                          <th className="text-right p-2 text-sm font-semibold">Ownership</th>
                          <th className="text-right p-2 text-sm font-semibold">Effective Val.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conversion.results.map((result, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 font-medium">{result.safeName}</td>
                            <td className="text-right p-2">
                              ${(result.investmentAmount / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2">${result.conversionPrice.toFixed(4)}</td>
                            <td className="text-right p-2">
                              <Badge variant="secondary" className="text-xs">
                                {result.discount.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="text-right p-2">
                              {result.sharesReceived.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                            <td className="text-right p-2 font-semibold">
                              {result.ownershipPct.toFixed(2)}%
                            </td>
                            <td className="text-right p-2 text-success">
                              ${(result.effectiveValuation / 1000000).toFixed(2)}M
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Cap Table Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Post-Conversion Cap Table</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-semibold">Stakeholder</th>
                          <th className="text-right p-2 text-sm font-semibold">Shares</th>
                          <th className="text-right p-2 text-sm font-semibold">Ownership %</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-2 font-medium">Founders</td>
                          <td className="text-right p-2">
                            {conversion.founders.shares.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                          <td className="text-right p-2 font-semibold">
                            {conversion.founders.pct.toFixed(2)}%
                          </td>
                        </tr>
                        {conversion.results.map((result, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2">{result.safeName}</td>
                            <td className="text-right p-2">
                              {result.sharesReceived.toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </td>
                            <td className="text-right p-2">{result.ownershipPct.toFixed(2)}%</td>
                          </tr>
                        ))}
                        <tr className="border-b">
                          <td className="p-2">New Investors (Series A)</td>
                          <td className="text-right p-2">
                            {conversion.newInvestors.shares.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                          <td className="text-right p-2">
                            {conversion.newInvestors.pct.toFixed(2)}%
                          </td>
                        </tr>
                        <tr className="border-b bg-muted/50">
                          <td className="p-2">Option Pool (Reserved)</td>
                          <td className="text-right p-2">
                            {conversion.optionPool.shares.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                          <td className="text-right p-2">
                            {conversion.optionPool.pct.toFixed(2)}%
                          </td>
                        </tr>
                        <tr className="font-bold bg-muted">
                          <td className="p-2">Total</td>
                          <td className="text-right p-2">
                            {conversion.totalShares.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </td>
                          <td className="text-right p-2">100.00%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Ownership Visualization */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Ownership Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={ownershipData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {ownershipData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">SAFE vs. Round Valuation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={comparisonData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip
                          formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                        />
                        <Legend />
                        <Bar dataKey="Effective Valuation" fill="#8b5cf6" />
                        <Bar dataKey="Round Price" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Insights */}
              {conversion.results.some((r) => r.discount > 50) && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>High Discount Detected:</strong> One or more SAFEs are converting at
                    more than 50% discount to the round price due to low valuation caps. This
                    represents significant dilution for founders and new investors.
                  </AlertDescription>
                </Alert>
              )}

              {conversion.founders.pct < 50 && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Founder Control:</strong> After this round, founders will own{' '}
                    {conversion.founders.pct.toFixed(1)}% of the company. Consider whether
                    maintaining control is important for governance decisions.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
